import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import SessionTimer from "@/components/SessionTimer";
import AppLayout from "@/components/layout/AppLayout";
import { PdfViewerHost } from "@/components/PdfViewerHost";
import { TableTools } from "@/components/TableTools";
import Login from "@/pages/Login";
import ChangePassword from "@/pages/ChangePassword";
import Dashboard from "@/pages/Dashboard";
import ManagerDashboard from "@/pages/ManagerDashboard";
import AttendanceCalendar from "@/pages/AttendanceCalendar";
import StaffSituation from "@/pages/StaffSituation";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import Employees from "@/pages/Employees";
import EmployeeAccounts from "@/pages/EmployeeAccounts";
import Requests from "@/pages/Requests";
import VacationCalendar from "@/pages/VacationCalendar";
import Certificates from "@/pages/Certificates";
import Reports from "@/pages/Reports";
import AccountantDetail from "@/pages/AccountantDetail";
import ImportEmployees from "@/pages/ImportEmployees";
import Users from "@/pages/Users";
import Settings from "@/pages/Settings";
import AuditLog from "@/pages/AuditLog";
import MenuConfig from "@/pages/MenuConfig";
import MyArea from "@/pages/MyArea";
import Departments from "@/pages/Departments";
import Documents from "@/pages/Documents";

import Safety from "@/pages/Safety";
import PpeDeliveries from "@/pages/PpeDeliveries";
import SupervisorRegister from "@/pages/SupervisorRegister";
import Weights from "@/pages/Weights";
import WeightsGrid from "@/pages/WeightsGrid";
import DisossoControl from "@/pages/DisossoControl";
import ChecklistArchive from "@/pages/ChecklistArchive";
import WeightsSplit from "@/pages/WeightsSplit";
import WeightsView from "@/pages/WeightsView";
import MyWeights from "@/pages/MyWeights";
import MyPpe from "@/pages/MyPpe";
import MySickness from "@/pages/MySickness";
import Supervisors from "@/pages/Supervisors";
import SafetyDocs from "@/pages/SafetyDocs";
import EmergencyRegister from "@/pages/EmergencyRegister";
import MyDocuments from "@/pages/MyDocuments";
import Piecework from "@/pages/Piecework";
import MonthClose from "@/pages/MonthClose";
import Archive from "@/pages/Archive";
import HomeMenu from "@/pages/HomeMenu";
import WorkTools from "@/pages/WorkTools";
import ToolDeliveries from "@/pages/ToolDeliveries";
import Tables from "@/pages/Tables";
import Companies from "@/pages/Companies";

const STAFF = ["admin", "titolare", "manager", "rspp"];
const Loading = () => (
  <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Caricamento...</div>
);

const OWNER_ROUTES = ["/", "/presenze", "/situazione", "/richieste", "/calendario-ferie", "/dipendenti",
                      "/documenti", "/archivio", "/impostazioni",
                      "/pesi/consultazione", "/pesi/cottimisti", "/pesi/report"];

function Protected({ children, roles = [...STAFF, "employee"] }) {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.must_change_password) return <ChangePassword />;
  if (user.role === "titolare" && !OWNER_ROUTES.includes(pathname)) {
    return <Navigate to="/" replace />;
  }
  if (!roles.includes(user.role)
      && !(user.role === "titolare" && (roles.includes("manager") || roles.includes("admin")))) {
    return <Navigate to="/" replace />;
  }
  return <AppLayout><ErrorBoundary>{children}</ErrorBoundary></AppLayout>;
}

function PieceworkOnly({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "employee" && !user.is_piecework) return <Navigate to="/mia-area" replace />;
  return <Protected roles={["employee"]}>{children}</Protected>;
}

function LoginRoute() {  const { user, loading } = useAuth();
  if (loading) return <Loading />;
  if (user) return <Navigate to="/" replace />;
  return <Login />;
}

function Home() {
  return <Protected roles={[...STAFF, "employee"]}><HomeMenu /></Protected>;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <SessionTimer />
        <PdfViewerHost />
        <TableTools />
        <Routes>
          <Route path="/login" element={<LoginRoute />} />
          <Route path="/" element={<Home />} />
          <Route path="/appello" element={<Protected roles={STAFF}><ManagerDashboard /></Protected>} />
          <Route path="/presenze" element={<Protected roles={STAFF}><AttendanceCalendar /></Protected>} />
          <Route path="/situazione" element={<Protected roles={STAFF}><StaffSituation /></Protected>} />
          <Route path="/inserimento" element={<Navigate to="/appello" replace />} />
          <Route path="/dipendenti" element={<Protected roles={STAFF}><Employees /></Protected>} />
          <Route path="/account-dipendenti" element={<Protected roles={["admin", "manager"]}><EmployeeAccounts /></Protected>} />
          <Route path="/richieste" element={<Protected><Requests /></Protected>} />
          <Route path="/calendario-ferie" element={<Protected roles={STAFF}><VacationCalendar /></Protected>} />
          <Route path="/certificati" element={<Protected roles={STAFF}><Certificates /></Protected>} />
          <Route path="/reparti" element={<Protected roles={["admin", "manager"]}><Departments /></Protected>} />
          <Route path="/documenti" element={<Protected roles={STAFF}><Documents /></Protected>} />
          <Route path="/report" element={<Protected roles={STAFF}><Reports kind="monthly" /></Protected>} />
          <Route path="/dettaglio-giornaliero" element={<Protected roles={STAFF}><AccountantDetail /></Protected>} />
          <Route path="/report-fine-mese" element={<Protected roles={STAFF}><MonthClose /></Protected>} />
          <Route path="/archivio" element={<Protected><Archive /></Protected>} />
          <Route path="/sicurezza/documenti-rspp" element={<Protected roles={["admin", "manager", "rspp"]}><SafetyDocs /></Protected>} />
          <Route path="/preposti" element={<Protected roles={["admin"]}><Supervisors /></Protected>} />
          <Route path="/emergenze" element={<Protected roles={["admin", "manager", "titolare", "rspp", "employee"]}><EmergencyRegister /></Protected>} />
          <Route path="/mia-area" element={<Protected roles={["employee"]}><MyArea /></Protected>} />
          <Route path="/malattia" element={<Protected roles={["employee"]}><MySickness /></Protected>} />
          <Route path="/miei-documenti" element={<Protected roles={["employee"]}><MyDocuments /></Protected>} />
          <Route path="/importa" element={<Protected roles={["admin"]}><ImportEmployees /></Protected>} />
          <Route path="/utenti" element={<Protected roles={["admin"]}><Users /></Protected>} />
          <Route path="/impostazioni" element={<Protected roles={["admin", "titolare"]}><Settings /></Protected>} />
          <Route path="/audit" element={<Protected roles={["admin"]}><AuditLog /></Protected>} />
          <Route path="/configurazione-menu" element={<Protected roles={["admin"]}><MenuConfig /></Protected>} />
          <Route path="/sicurezza/dpi" element={<Protected roles={STAFF}><Safety tab="ppe" /></Protected>} />
          <Route path="/sicurezza/firme" element={<Navigate to="/sicurezza/dpi" replace />} />
          <Route path="/sicurezza/scadenze" element={<Navigate to="/sicurezza/dpi" replace />} />
          <Route path="/sicurezza/consegne" element={<Protected roles={STAFF}><PpeDeliveries /></Protected>} />
          <Route path="/sicurezza/attrezzature" element={<Protected roles={STAFF}><WorkTools /></Protected>} />
          <Route path="/sicurezza/consegne-attrezzature" element={<Protected roles={STAFF}><ToolDeliveries /></Protected>} />
          <Route path="/sicurezza/registro" element={<Protected roles={STAFF}><SupervisorRegister /></Protected>} />
          <Route path="/sicurezza/assegnazioni" element={<Protected roles={STAFF}><Safety tab="assignments" /></Protected>} />
          <Route path="/pesi/registro" element={<Protected roles={STAFF}><WeightsGrid /></Protected>} />
          <Route path="/pesi/controllo-disosso" element={<Protected roles={["admin", "manager"]}><DisossoControl /></Protected>} />
          <Route path="/checklist-pulizie" element={<Navigate to="/checklist-pulizie/archivio" replace />} />
          <Route path="/checklist-pulizie/archivio" element={<Protected roles={["admin", "manager"]}><ChecklistArchive /></Protected>} />
          <Route path="/pesi/divisione" element={<Protected roles={STAFF}><WeightsSplit /></Protected>} />
          <Route path="/pesi/consultazione" element={<Protected roles={STAFF}><WeightsView /></Protected>} />
          <Route path="/pesi/cottimisti" element={<Protected roles={STAFF}><Piecework /></Protected>} />
          <Route path="/pesi/lavorazioni" element={<Protected roles={STAFF}><Weights tab="products" /></Protected>} />          <Route path="/pesi/report" element={<Protected roles={STAFF}><Weights tab="report" /></Protected>} />
          <Route path="/miei-pesi" element={<PieceworkOnly><MyWeights /></PieceworkOnly>} />
          <Route path="/miei-dpi" element={<Protected roles={["employee"]}><MyPpe /></Protected>} />
          <Route path="/dashboard" element={<Protected roles={STAFF}><Dashboard /></Protected>} />
          <Route path="/aziende" element={<Protected roles={["admin", "manager"]}><Companies /></Protected>} />
          <Route path="/tabelle" element={<Protected roles={["admin", "manager"]}><Tables /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
